import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { LoginScreen } from './auth/LoginScreen';
import { SignupScreen } from './auth/SignupScreen';
import { OnboardingWizard } from './auth/OnboardingWizard';
import { TrialBanner } from './auth/TrialBanner';
// V222 (C-03/C-17) — banner de facturación (factura global sin emisor, CSD vencido/por vencer).
import { CfdiNoticeBanner } from './auth/CfdiNoticeBanner';
import { SignupIdentifierNotice } from './auth/SignupIdentifierNotice';
import { TrialExpiredScreen } from './auth/TrialExpiredScreen';
import { onTrialExpired } from './lib/api';
import { LegalRouter, isLegalRoute } from './legal/LegalRouter';
import { PublicAuthRouter, isPublicAuthRoute } from './auth/PublicAuthRouter';
import { UpdateCardRedirect } from './auth/UpdateCardRedirect';
import { isReactivatePath, isUpdateCardPath } from './auth/updateCardLink';
// V70 — canal público de booking (/reservar/:key + /reserva/:token), sin login.
import { PublicBookingRouter, isPublicBookingRoute } from './booking/PublicBookingPage';
import { PublicStorePage, isPublicStoreRoute } from './store/PublicStorePage';
// V92 — baja pública de comunicados (/baja/:token), sin login.
import { UnsubscribePage, isUnsubscribeRoute } from './comms/UnsubscribePage';
// Fase 2 — autofactura pública (/factura/:code), sin login.
import { SelfInvoicePage, isSelfInvoiceRoute } from './cfdi/SelfInvoicePage';
// PR2/F — aviso de privacidad hospedado (/aviso/:slug), sin login.
import { AvisoPage, isAvisoRoute } from './cfdi/AvisoPage';
// V205 — Fidelidad pública: alta (/fidelidad/:slug) y cartilla (/fidelidad/c/:código), sin login.
import { FidelidadPage, isFidelidadRoute } from './loyalty/FidelidadPage';
// V142 — portal público de cotizaciones (/cotizacion/:token), sin login.
import { QuotePortalPage, isQuotePortalRoute } from './quotes/QuotePortalPage';
import { PlatformConsolePage, isPlatformRoute } from './platform/PlatformConsolePage';
import { ChangePasswordForcedScreen } from './auth/ChangePasswordForcedScreen';
import { EmailConfirmationBanner } from './auth/EmailConfirmationBanner';
// V79 — gate de re-aceptación legal (docs v2.2): bloquea hasta re-aceptar.
import { LegalReacceptanceGate } from './legal/LegalReacceptanceGate';
// Tus archivos existentes — coloca Dashboard.tsx y ThemeProvider.tsx en /src.
// Si aún están como .jsx, el shim en vite-env.d.ts permite la importación.
import { ThemeProvider } from './ThemeProvider';
import { ContactsProvider } from './contacts/useContacts';
import Dashboard from './Dashboard';

type AuthView = 'login' | 'signup';

/**
 * URL estable del formulario de registro. Un visitante anónimo que entra a
 * `/signup` (deep-link desde el landing getstratos.app) ve el formulario "Crea tu
 * workspace" directo; la raíz `/` sigue mostrando el login. NO es una ruta pública
 * tipo `/legal` o `/auth/*`: vive DENTRO de AuthProvider porque (a) el signup
 * auto-loguea vía useAuth y (b) un usuario YA autenticado en `/signup` debe caer a
 * la app. Por eso se resuelve aquí en Shell con el toggle existente, no en
 * PublicAuthRouter. Sin react-router: navegación por pathname + history API.
 */
const SIGNUP_PATH = '/signup';
const isSignupPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '') === SIGNUP_PATH;

function Shell() {
  const { token, user, loading, logout, refresh } = useAuth();
  // Deep-link: si la entrada es `/signup`, arranca en el formulario de registro.
  const [authView, setAuthView] = useState<AuthView>(
    () => (isSignupPath(window.location.pathname) ? 'signup' : 'login'),
  );

  // Mantiene la URL en sync con el toggle (sin ensuciar el historial): elegir
  // "Regístrate" deja la URL en `/signup` (compartible) y volver al login en `/`.
  const goToView = (view: AuthView) => {
    setAuthView(view);
    const path = view === 'signup' ? SIGNUP_PATH : '/';
    if (window.location.pathname.replace(/\/+$/, '') !== path.replace(/\/+$/, '')) {
      window.history.replaceState(null, '', path);
    }
  };

  // Usuario YA autenticado que aterriza en `/signup` → lo mandamos a la app
  // limpiando la URL (mismo efecto que el guard de hoy: con sesión se ve el app).
  useEffect(() => {
    if (token && user && isSignupPath(window.location.pathname)) {
      window.history.replaceState(null, '', '/');
    }
  }, [token, user]);
  const [trialExpired, setTrialExpired] = useState<{
    expiredAt: string | null;
    message: string;
  } | null>(null);

  // Si CUALQUIER llamada al backend devuelve 403 TRIAL_EXPIRED, mostramos
  // la pantalla bloqueante. El listener vive en api.ts.
  useEffect(() => {
    const off = onTrialExpired((info) => setTrialExpired(info));
    return () => { off(); };
  }, []);

  if (loading && !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-sm text-slate-500">
        Cargando…
      </div>
    );
  }

  // Sin sesión → login o signup según el toggle / la URL (`/signup` deep-link).
  if (!token || !user) {
    return authView === 'signup' ? (
      <SignupScreen onGoToLogin={() => goToView('login')} />
    ) : (
      <LoginScreenWithSignup onGoToSignup={() => goToView('signup')} />
    );
  }

  // v1.3.2 — must_change_password (flujo Opción B): gate antes de todo lo demás.
  // No es saltable: el user no entra al app hasta cambiar la contraseña temporal.
  if (user.mustChangePassword) {
    return <ChangePasswordForcedScreen onLogout={logout} />;
  }

  // V79 — gate de RE-ACEPTACIÓN legal: /me dice que el usuario aceptó una
  // versión vieja de los T&C/Aviso (o ninguna) → debe aceptar los vigentes
  // antes de seguir. No saltable, pero puede cerrar sesión. Los signups nuevos
  // ya aceptan la versión vigente al registrarse → nunca lo ven.
  if (user.legal?.needsReacceptance) {
    return (
      <LegalReacceptanceGate
        onAccepted={async () => { await refresh(); }}
        onLogout={logout}
      />
    );
  }

  // T4 — liga del correo de cobro rechazado: abre el portal de Stripe. Va ANTES del gate de
  // suspensión: un negocio suspendido por falta de pago también debe poder poner tarjeta.
  if (isUpdateCardPath(window.location.pathname)) {
    return <UpdateCardRedirect isAdmin={user.role === 'SUPER_USER'} />;
  }

  // §17.6 — Gate de expiración del trial. PROACTIVO: si /me dice expired && !active,
  // bloqueamos ANTES de entrar al app (no esperamos a un 403). Belt-and-suspenders
  // con el listener reactivo de 403 TRIAL_EXPIRED (api.ts) que setea `trialExpired`.
  const ts = user.tenant.trialStatus;
  const trialBlockedByMe = !!ts && ts.expired && !ts.active;
  // T4 — suspendida por cobranza: /me trae suspendedAt aunque la prueba no haya "expirado".
  const suspendedByMe = !!user.tenant.billing?.suspendedAt && !ts?.active;
  const reactivateLink = isReactivatePath(window.location.pathname);
  if (reactivateLink && !(trialExpired || trialBlockedByMe || suspendedByMe)) {
    // Liga del último correo con la cuenta ya al corriente: no hay nada que pagar.
    window.history.replaceState(null, '', '/');
  }
  if (trialExpired || trialBlockedByMe || suspendedByMe) {
    const expiredAt = trialExpired?.expiredAt
      ?? (user.tenant.trialEndsAt ? user.tenant.trialEndsAt.slice(0, 10) : null);
    const message = trialExpired?.message
      ?? 'Tu prueba terminó. Activa tu plan para recuperar el acceso.';
    return (
      <TrialExpiredScreen
        me={user}
        expiredAt={expiredAt}
        message={message}
        // 1a: el flujo de suscripción (pricing + checkout) es del admin; a los demás
        // roles se les muestra solo transferencia/contacto (el backend igual 403ea).
        // Kill-switch server-side (FAIL-CLOSED): sin BILLING_CHECKOUT_ENABLED=true
        // el paywall nuevo queda dormido (prod hasta el go-live 1c).
        canSubscribe={user.role === 'SUPER_USER' && !!user.billing?.checkoutEnabled}
        openReactivate={reactivateLink}
        onLogout={() => {
          setTrialExpired(null);
          logout();
        }}
      />
    );
  }

  // Con sesión, pero sin onboarding completado → wizard.
  if (!user.tenant.onboardingCompletedAt) {
    return (
      <>
        <SignupIdentifierNotice />
        <OnboardingWizard
          me={user}
          onComplete={async () => {
            await refresh();
          }}
        />
      </>
    );
  }

  // Consola /platform (Entrega 1a) — solo SUPER_USER; el backend enforca
  // platform-admin (403 → la consola muestra "requiere platform-admin").
  if (isPlatformRoute(window.location.pathname) && user.role === 'SUPER_USER') {
    return (
      <ThemeProvider initialMe={user as any}>
        <PlatformConsolePage onLogout={logout} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider initialMe={user as any}>
      {/* v1.3.2 — banner persistente si el email no está confirmado (no bloquea). */}
      {!user.emailConfirmed && <EmailConfirmationBanner />}
      <TrialBanner me={user} />
      <CfdiNoticeBanner me={user} />
      <SignupIdentifierNotice />
      {/* ContactsProvider: fuente única de personas para TODOS los selectores
          (CRM, leads, Boutique). Solo se monta autenticado + onboarded. */}
      <ContactsProvider>
        {/* Cast a any: Dashboard.tsx tiene props sin tipo explícito porque
            coexiste con consumidores .jsx — el shim del vite-env lo absorbe. */}
        {(() => {
          const D = Dashboard as unknown as React.FC<{ onLogout: () => void; me: any }>;
          return <D onLogout={logout} me={user as any} />;
        })()}
      </ContactsProvider>
    </ThemeProvider>
  );
}

/**
 * LoginScreen no tiene prop para mostrar "¿No tienes cuenta?" en su API actual,
 * así que lo envolvemos con un pie de página que swappea a SignupScreen.
 * Mantiene LoginScreen como un componente puro.
 */
function LoginScreenWithSignup({ onGoToSignup }: { onGoToSignup: () => void }) {
  return (
    <div className="relative">
      <LoginScreen />
      <div className="pointer-events-none fixed inset-x-0 bottom-6 grid place-items-center">
        <button
          onClick={onGoToSignup}
          className="pointer-events-auto rounded-full bg-white px-4 py-2 text-xs font-medium text-slate-700 shadow ring-1 ring-slate-200 hover:bg-slate-50"
        >
          ¿No tienes cuenta? <span className="ml-1 text-slate-900 underline">Regístrate</span>
        </button>
      </div>
    </div>
  );
}

export default function App() {
  // Páginas legales públicas (/legal/*): se renderizan ANTES y FUERA de
  // AuthProvider → sin sesión, sin TenantInterceptor, sin TrialGuard. Lectura
  // única del pathname; la navegación a/desde estas rutas usa anchors (recarga
  // completa), así que App se reevalúa. No requiere react-router.
  if (isLegalRoute(window.location.pathname)) {
    return <LegalRouter />;
  }

  // Rutas de auth públicas (forgot/reset password, v1.3.1): mismo trato que
  // /legal — sin AuthProvider, sin TenantInterceptor, sin TrialGuard.
  // Rutas de auth públicas (v1.3.2: setup-account / confirm-email): fuera de
  // AuthProvider, igual que /legal.
  if (isPublicAuthRoute(window.location.pathname)) {
    return <PublicAuthRouter />;
  }

  // V70 — reservas públicas (link/QR): mismo trato que /legal y /auth/* — fuera
  // de AuthProvider, sin sesión; el tenant se resuelve por la key en el backend.
  if (isPublicBookingRoute(window.location.pathname)) {
    return <PublicBookingRouter />;
  }

  // V92 — baja de comunicados (link del correo): pública, por token capability.
  if (isUnsubscribeRoute(window.location.pathname)) {
    return <UnsubscribePage />;
  }

  // Fase 2 — autofactura (/factura/:code, QR del ticket): pública, el tenant
  // se resuelve por el short-code no-adivinable en el backend.
  if (isSelfInvoiceRoute(window.location.pathname)) {
    return <SelfInvoicePage />;
  }

  // PR2/F — aviso de privacidad hospedado (/aviso/:slug, link/QR del negocio):
  // público, el tenant se resuelve por su slug en el backend (fn_public_aviso).
  if (isAvisoRoute(window.location.pathname)) {
    return <AvisoPage />;
  }

  // V205 — Fidelidad (QR del PDF → alta; cartilla por código). Público; el tenant se
  // resuelve en el backend (fns DEFINER por slug / por código).
  if (isFidelidadRoute(window.location.pathname)) {
    return <FidelidadPage />;
  }

  // V142 — portal público de la cotización (/cotizacion/:token): el cliente ve
  // y acepta/rechaza sin login; el tenant se resuelve por el token no-adivinable.
  if (isQuotePortalRoute(window.location.pathname)) {
    return <QuotePortalPage />;
  }

  // V155 — tienda pública de catálogo (/<slug>/tienda): sin login; el tenant se
  // resuelve por su slug en el backend. Se evalúa AL FINAL (patrón /<slug>) para
  // no sombrear los prefijos reservados de las rutas públicas de arriba.
  if (isPublicStoreRoute(window.location.pathname)) {
    return <PublicStorePage />;
  }

  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
